/* "Copy chart to…": the chart on screen, into another of the user's collections. What is
   copied is what is shown, this device's edits included, and the copy remembers the row it
   came from. A collection that already holds the spot is asked about before it is replaced. */

import { dataMessage, type Collection } from "@preflop/api";
import { opponentOf } from "@preflop/core";
import { el, fill } from "../../ui/dom";
import { flash } from "./flash";
import { page } from "./state";

const NEW = "new";

/** A select dressed as a button: its first entry is its label, and it snaps back to it. */
export const copyTo = el("select", {
  class: "copy",
  title: "Copy the chart on screen into another of your collections",
  ariaLabel: "Copy chart to a collection",
  hidden: true,
  dataset: { testid: "copy-to" },
});

function targets(): Collection[] {
  const { collections, collection, user } = page().session;
  // The user's own, never a public collection of someone else's that happens to be active.
  return collections.filter((c) => c.ownerId === user.id && c.id !== collection.id);
}

async function copyInto(target: Collection): Promise<void> {
  const { spot, session } = page();
  if (!spot) return;
  // A variation says what it is a variation of, so its profile goes along: a target whose
  // list does not know it is given it, name, colour and note, as the chart lands.
  const profile = opponentOf(session.collection.opponents, spot.profile);
  let done = await session.api.copySpot(target.id, spot, { profile });
  if (done === "exists") {
    if (!confirm(`"${target.name}" already has a chart for this spot. Replace it with this one?`))
      return flash(`"${target.name}" was left as it is.`, "info");
    done = await session.api.copySpot(target.id, spot, { replace: true, profile });
  }
  flash(`${done === "replaced" ? "Replaced in" : "Copied to"} "${target.name}".`, "info");
}

copyTo.addEventListener("change", () => {
  const chosen = copyTo.value;
  copyTo.value = "";
  if (chosen === NEW) return void location.assign("./collections.html");
  const target = targets().find((c) => c.id === chosen);
  if (!target) return;
  copyTo.disabled = true;
  copyInto(target)
    .catch((e: unknown) => flash(dataMessage(e), "err"))
    .finally(() => (copyTo.disabled = false));
});

/** Shown whenever there is a chart to copy. With nowhere to copy it to yet, the one entry
    leads to where a collection is made. */
export function renderCopyTo(): void {
  const { spot, creator } = page();
  copyTo.hidden = !spot || !!creator;
  if (copyTo.hidden) return;
  fill(
    copyTo,
    el("option", { value: "", hidden: true }, "Copy chart to…"),
    // As text, like every name a user wrote.
    targets().map((c) => el("option", { value: c.id }, c.name)),
    el("option", { value: NEW }, "New collection…"),
  );
  copyTo.value = "";
}
